import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api-error";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ArrowRight, Filter, X } from "lucide-react";
import { createPayment } from "@/lib/api";
import { EUR, fmtDate, isoLocalDate, num, paymentMethodOptions } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";

/**
 * Drill-down for the "Da pagare" tile.
 *
 * Shows two stacked sections for the selected period:
 *  1. "Per categoria" — compact horizontal-bar breakdown of the residual amount
 *     by category, sorted DESC. Click a category to filter the deadline list.
 *  2. "Scadenze non saldate" — table of unpaid (planned / partially_paid)
 *     occurrences in the window, filtered by the selected category if any.
 *
 * Both sections honour the same exclusion rule as the panel: cancelled and
 * skipped occurrences are NEVER counted (they're audit-only, no real exposure).
 */
export function RemainingExposureDrillDown({
  open,
  onOpenChange,
  rows,
  categories,
  vendors,
  expenses,
  from,
  to,
  periodLabel,
  totalRemaining,
  onChanged,
  initialCategoryId = null,
  onlyOverdue = false,
  title = "Da pagare — dettaglio del periodo",
}) {
  const [filterCatId, setFilterCatId] = useState(initialCategoryId);

  // Sync the preset category whenever the dialog re-opens with a new initial
  // value (e.g., user clicks a different category in "Residuo per categoria").
  useEffect(() => {
    if (open) setFilterCatId(initialCategoryId);
  }, [open, initialCategoryId]);

  // Inline "Paga rapido" dialog state.
  const [payRow, setPayRow] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payDate, setPayDate] = useState(() => isoLocalDate(new Date()));
  const [payMethod, setPayMethod] = useState("bank_transfer");
  const [payRef, setPayRef] = useState("");
  const [paying, setPaying] = useState(false);

  const catMap = useMemo(
    () => Object.fromEntries((categories || []).map((c) => [c.id, c])),
    [categories],
  );
  const venMap = useMemo(
    () => Object.fromEntries((vendors || []).map((v) => [v.id, v])),
    [vendors],
  );
  const expMap = useMemo(
    () => Object.fromEntries((expenses || []).map((e) => [e.id, e])),
    [expenses],
  );

  // Items contributing to financial exposure.
  // When onlyOverdue is enabled, restrict to past-due unpaid items so the
  // dialog shows the overdue-specific KPI breakdown + deadline list.
  const todayIso = new Date().toISOString().slice(0, 10);
  const contributing = useMemo(
    () =>
      (rows || []).filter((r) => {
        if (r.status === "cancelled" || r.status === "skipped") return false;
        if (onlyOverdue) {
          if (r.status !== "planned" && r.status !== "partially_paid") return false;
          if (!r.due_date || r.due_date >= todayIso) return false;
        }
        return true;
      }),
    [rows, onlyOverdue, todayIso],
  );

  // Aggregate residual by effective_category_id.
  const breakdown = useMemo(() => {
    const acc = new Map(); // catId -> { remaining, count, planned, paid }
    for (const r of contributing) {
      const remaining = num(r.effective_amount) - num(r.total_paid);
      if (remaining <= 0.005) continue; // fully paid → no exposure to break down
      const cid = r.effective_category_id || "__none__";
      const cur = acc.get(cid) || { remaining: 0, count: 0, planned: 0, paid: 0 };
      cur.remaining += remaining;
      cur.planned += num(r.effective_amount);
      cur.paid += num(r.total_paid);
      cur.count += 1;
      acc.set(cid, cur);
    }
    const arr = Array.from(acc.entries()).map(([id, agg]) => ({
      id,
      name: catMap[id]?.name || "Senza categoria",
      ...agg,
    }));
    arr.sort((a, b) => b.remaining - a.remaining);
    return arr;
  }, [contributing, catMap]);

  const maxRemaining = breakdown[0]?.remaining || 1;

  // Unpaid occurrences for the deadline table.
  const unpaid = useMemo(() => {
    const list = contributing.filter(
      (r) => r.status === "planned" || r.status === "partially_paid",
    );
    list.sort((a, b) => (a.due_date || "").localeCompare(b.due_date || ""));
    return list;
  }, [contributing]);

  const filteredUnpaid = useMemo(() => {
    if (!filterCatId) return unpaid;
    return unpaid.filter(
      (r) => (r.effective_category_id || "__none__") === filterCatId,
    );
  }, [unpaid, filterCatId]);

  const filteredCount = filteredUnpaid.length;
  const filteredRemaining = useMemo(() => {
    let s = 0;
    for (const r of filteredUnpaid) s += num(r.effective_amount) - num(r.total_paid);
    return s;
  }, [filteredUnpaid]);

  const handleClose = (next) => {
    if (!next) setFilterCatId(null); // reset filter on close
    onOpenChange(next);
  };

  /* ---- Paga rapido ----------------------------------------------------- */
  const openPay = (row) => {
    const remaining = num(row.effective_amount) - num(row.total_paid);
    setPayRow(row);
    setPayAmount(
      remaining > 0 ? remaining.toFixed(2) : Number(row.effective_amount).toFixed(2),
    );
    setPayDate(isoLocalDate(new Date()));
    setPayMethod("bank_transfer");
    setPayRef("");
  };

  const closePay = () => {
    setPayRow(null);
    setPaying(false);
  };

  const submitPay = async () => {
    if (!payRow || paying) return;
    if (!payAmount || Number(payAmount) <= 0) {
      toast.error("Importo non valido");
      return;
    }
    setPaying(true);
    try {
      await createPayment({
        occurrence_id: payRow.id,
        paid_amount: String(payAmount),
        paid_at: payDate,
        method: payMethod,
        reference: payRef || null,
      });
      toast.success("Pagamento registrato.");
      closePay();
      // Notify parent so totals/breakdown/dashboard KPI all refresh.
      // The drill-down stays open by design — the user can keep working
      // through the remaining items without re-navigating.
      if (onChanged) await onChanged();
    } catch (e) {
      setPaying(false);
      toast.error(formatApiError(e, "Errore registrazione pagamento"));
    }
  };

  const titleOf = (occ) => {
    if (occ.effective_title) return occ.effective_title;
    return expMap[occ.expense_id]?.title || "—";
  };

  return (
    <>
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="remaining-drilldown-dialog"
        className="max-w-3xl"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-2">
            <span>{title}</span>
            <span
              data-testid="remaining-drilldown-total"
              className="font-mono text-base tabular-nums text-amber-900"
            >
              {EUR(totalRemaining)}
            </span>
          </DialogTitle>
          <DialogDescription>
            Periodo: <strong>{periodLabel}</strong> · Esclude voci annullate o saltate
          </DialogDescription>
        </DialogHeader>

        {/* Section 1: breakdown by category */}
        <section data-testid="remaining-drilldown-breakdown" className="mt-2">
          <header className="mb-2 flex items-center justify-between">
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-stone-600">
              Da pagare per categoria
            </h3>
            {filterCatId && (
              <button
                type="button"
                data-testid="remaining-drilldown-clear-filter"
                onClick={() => setFilterCatId(null)}
                className="inline-flex items-center gap-1 text-[11px] text-stone-600 hover:text-stone-900"
              >
                <X size={11} /> Rimuovi filtro categoria
              </button>
            )}
          </header>
          {breakdown.length === 0 ? (
            <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs text-emerald-800">
              Nessun residuo da pagare nel periodo. Tutto saldato.
            </p>
          ) : (
            <ul className="max-h-48 divide-y divide-stone-100 overflow-y-auto rounded-lg border border-stone-200 bg-white">
              {breakdown.map((b) => {
                const pct = (b.remaining / maxRemaining) * 100;
                const isActive = filterCatId === b.id;
                return (
                  <li
                    key={b.id}
                    data-testid={`remaining-drilldown-cat-${b.id}`}
                  >
                    <button
                      type="button"
                      onClick={() => setFilterCatId(isActive ? null : b.id)}
                      className={
                        "flex w-full flex-col gap-1 px-3 py-2 text-left transition-colors " +
                        (isActive
                          ? "bg-amber-50"
                          : "hover:bg-stone-50")
                      }
                    >
                      <div className="flex items-baseline justify-between gap-3 text-sm">
                        <span
                          className={
                            "truncate " +
                            (isActive ? "font-semibold text-amber-900" : "text-stone-800")
                          }
                        >
                          {b.name}
                          {isActive && (
                            <span className="ml-1.5 text-[10px] uppercase tracking-wider text-amber-700">
                              · attivo
                            </span>
                          )}
                        </span>
                        <span className="flex items-center gap-2 font-mono text-sm tabular-nums">
                          <span
                            className={
                              isActive
                                ? "text-amber-900 font-semibold"
                                : "text-stone-900 font-medium"
                            }
                          >
                            {EUR(b.remaining)}
                          </span>
                          <span className="text-[10px] tabular-nums text-stone-400">
                            ({b.count})
                          </span>
                        </span>
                      </div>
                      <div className="h-1 w-full overflow-hidden rounded-full bg-stone-100/80">
                        <div
                          className={
                            "h-full rounded-full " +
                            (isActive ? "bg-amber-500" : "bg-amber-300")
                          }
                          style={{ width: `${Math.max(8, pct)}%`, maxWidth: "100%" }}
                        />
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Section 2: unpaid deadlines (filtered by selected category if any) */}
        <section data-testid="remaining-drilldown-deadlines" className="mt-4">
          <header className="mb-2 flex items-center justify-between">
            <h3 className="text-[11px] font-semibold uppercase tracking-wider text-stone-600">
              Scadenze non saldate
              {filterCatId && (
                <span className="ml-1.5 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] uppercase tracking-wider text-amber-800">
                  <Filter size={9} />
                  {catMap[filterCatId]?.name || "Senza categoria"}
                </span>
              )}
            </h3>
            <span className="text-[11px] text-stone-500">
              {filteredCount} {filteredCount === 1 ? "voce" : "voci"} ·{" "}
              <span className="font-mono tabular-nums">{EUR(filteredRemaining)}</span>
            </span>
          </header>
          <div className="max-h-72 overflow-y-auto rounded-lg border border-stone-200 bg-white">
            <Table>
              <TableHeader className="sticky top-0 bg-white">
                <TableRow>
                  <TableHead className="w-[110px]">Scadenza</TableHead>
                  <TableHead>Descrizione</TableHead>
                  <TableHead className="w-[110px]">Stato</TableHead>
                  <TableHead className="w-[110px] text-right">Residuo</TableHead>
                  <TableHead className="w-[80px] text-right">Azione</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUnpaid.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="py-6 text-center text-xs text-stone-500"
                    >
                      Nessuna voce non saldata
                      {filterCatId
                        ? " per la categoria selezionata."
                        : " nel periodo."}
                    </TableCell>
                  </TableRow>
                )}
                {filteredUnpaid.map((r) => {
                  const remaining = num(r.effective_amount) - num(r.total_paid);
                  const overdue =
                    (r.status === "planned" || r.status === "partially_paid") &&
                    r.due_date < todayIso;
                  return (
                    <TableRow
                      key={r.id}
                      data-testid={`remaining-drilldown-row-${r.id}`}
                    >
                      <TableCell
                        className={
                          "font-mono text-xs tabular-nums " +
                          (overdue ? "font-semibold text-rose-700" : "text-stone-700")
                        }
                      >
                        {fmtDate(r.due_date)}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm font-medium text-stone-900 truncate">
                          {titleOf(r)}
                        </div>
                        <div className="text-[11px] text-stone-500 truncate">
                          {venMap[r.effective_vendor_id]?.name || "Senza fornitore"}
                          {" · "}
                          {catMap[r.effective_category_id]?.name || "Senza categoria"}
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={r.status} />
                        {overdue && (
                          <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-wider text-rose-700">
                            Scaduta
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm tabular-nums text-amber-900">
                        {EUR(remaining)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`remaining-drilldown-pay-${r.id}`}
                          onClick={() => openPay(r)}
                          className="h-7 px-2 text-xs"
                        >
                          Paga
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </section>

        <DialogFooter className="mt-3 flex-row items-center justify-between sm:justify-between">
          <span className="text-[11px] text-stone-500">
            {fmtDate(from)} → {fmtDate(to)}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => handleClose(false)}>
              Chiudi
            </Button>
            <Button asChild data-testid="remaining-drilldown-open-occurrences">
              <Link
                to={
                  `/occurrences?date_from=${from}&date_to=${to}` +
                  (filterCatId && filterCatId !== "__none__"
                    ? `&category_id=${filterCatId}`
                    : "")
                }
              >
                Apri in Scadenze <ArrowRight size={12} className="ml-1" />
              </Link>
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Inline "Paga rapido" dialog. Reuses createPayment to avoid forking the
        payment flow; the parent panel reloads via onChanged() on success so
        all surfaces (panel KPIs, breakdown, deadline list) refresh together. */}
    <Dialog open={!!payRow} onOpenChange={(o) => { if (!o) closePay(); }}>
      <DialogContent data-testid="remaining-drilldown-pay-dialog">
        <DialogHeader>
          <DialogTitle>Registra pagamento</DialogTitle>
          <DialogDescription>
            Anche pagamenti parziali sono supportati. Lo stato e i totali vengono ricalcolati automaticamente.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Importo</Label>
            <Input
              type="number"
              step="0.01"
              data-testid="remaining-drilldown-pay-amount"
              value={payAmount}
              onChange={(e) => setPayAmount(e.target.value)}
            />
            {payRow && (
              <p className="mt-1 text-xs text-stone-500">
                Pianificato {EUR(payRow.effective_amount)} — già pagato {EUR(payRow.total_paid || 0)}
              </p>
            )}
          </div>
          <div>
            <Label>Data pagamento (cassa)</Label>
            <Input
              type="date"
              data-testid="remaining-drilldown-pay-date"
              value={payDate}
              onChange={(e) => setPayDate(e.target.value)}
            />
          </div>
          <div>
            <Label>Metodo di pagamento</Label>
            <Select value={payMethod} onValueChange={setPayMethod}>
              <SelectTrigger data-testid="remaining-drilldown-pay-method">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {paymentMethodOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Riferimento (es. CRO)</Label>
            <Input
              data-testid="remaining-drilldown-pay-ref"
              value={payRef}
              onChange={(e) => setPayRef(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={closePay} disabled={paying}>
            Annulla
          </Button>
          <Button
            data-testid="remaining-drilldown-pay-confirm"
            onClick={submitPay}
            disabled={paying}
          >
            {paying ? "Registrazione…" : "Registra"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
